import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import MenuEspace from "./MenuSpace";
import "./SupportReclamation.css";

// Problem type display mappings
const PROBLEM_TYPES = [
  { value: "commande_non_recue", label: "Commande non reçue", icon: "bi-box-seam" },
  { value: "devis_refuse", label: "Devis non accepté / refusé", icon: "bi-file-earmark-x" },
  { value: "paiement", label: "Problème de paiement", icon: "bi-credit-card" },
  { value: "article_endommage", label: "Article endommagé / incorrect", icon: "bi-shield-exclamation" },
  { value: "autre", label: "Autre demande", icon: "bi-chat-left-text" }
];

interface ReclamationData {
  id: number;
  CodeReclamation: string;
  VisiteurId: number | null;
  Nom: string;
  Email: string | null;
  Telephone: string | null;
  TypeProbleme: string;
  RefCommande: string | null;
  Description: string;
  ImagePath: string | null;
  Statut: string;
  Date: string;
  DateReponse: string | null;
  ReponseAdmin: string | null;
}

export default function SupportReclamation() {
  const { id: routeVisiteurId } = useParams();
  const location = useLocation();
  const storageVisiteurId = localStorage.getItem("VisiteurId");
  const visiteurId = routeVisiteurId || storageVisiteurId || "";
  const hideSidebar = new URLSearchParams(location.search).get("hideSidebar") === "true";
  const visiteurName = localStorage.getItem("VisiteurName") || "";
  const visiteurEmail = localStorage.getItem("VisiteurEmail") || ""; // if any
  const visiteurTele = localStorage.getItem("VisiteurTele") || "";

  const [activeTab, setActiveTab] = useState<"submit" | "track">("submit");

  // Form states
  const [nom, setNom] = useState(visiteurName);
  const [email, setEmail] = useState(visiteurEmail);
  const [telephone, setTelephone] = useState(visiteurTele);
  const [typeProbleme, setTypeProbleme] = useState("commande_non_recue");
  const [refCommande, setRefCommande] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Status states
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccessCode, setSubmitSuccessCode] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState("");

  // Tracking states
  const [trackCode, setTrackCode] = useState("");
  const [trackingResult, setTrackingResult] = useState<ReclamationData | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState("");

  // History list (logged-in user)
  const [myReclamations, setMyReclamations] = useState<ReclamationData[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load history if visiteurId exists
  useEffect(() => {
    if (visiteurId) {
      fetchHistory();
    }
  }, [visiteurId]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/visiteur/${visiteurId}/reclamations`
      );
      setMyReclamations(res.data);
    } catch (err) {
      console.error("Error fetching reclamation history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nom.trim()) {
      setSubmitError("Le nom complet est obligatoire.");
      return;
    }
    if (!description.trim()) {
      setSubmitError("Veuillez décrire le problème.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    const formData = new FormData();
    formData.append("Nom", nom);
    if (email) formData.append("Email", email);
    if (telephone) formData.append("Telephone", telephone);
    formData.append("TypeProbleme", typeProbleme);
    if (refCommande) formData.append("RefCommande", refCommande);
    formData.append("Description", description);
    if (visiteurId) formData.append("VisiteurId", visiteurId);
    if (image) formData.append("image", image);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/reclamations`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setSubmitSuccessCode(res.data.CodeReclamation);
      if (visiteurId) {
        fetchHistory(); // Refresh history list
      }
      // Reset form
      setDescription("");
      setRefCommande("");
      removeImage();
    } catch (err: any) {
      setSubmitError(
        err.response?.data?.message || "Une erreur s'est produite lors de l'envoi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleTrack = async (e?: React.FormEvent, codeToUse?: string) => {
    if (e) e.preventDefault();
    const queryCode = codeToUse || trackCode;
    if (!queryCode.trim()) {
      setTrackingError("Veuillez entrer un code de réclamation.");
      return;
    }

    setTrackingLoading(true);
    setTrackingError("");
    setTrackingResult(null);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/reclamations/track/${queryCode.trim().toUpperCase()}`
      );
      setTrackingResult(res.data);
    } catch (err: any) {
      setTrackingError(
        err.response?.data?.message || "Aucune réclamation trouvée pour ce code."
      );
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleSelectFromHistory = (code: string) => {
    setActiveTab("track");
    setTrackCode(code);
    handleTrack(undefined, code);
  };

  const handleCopyCode = () => {
    if (submitSuccessCode) {
      navigator.clipboard.writeText(submitSuccessCode);
      alert("Code de réclamation copié !");
    }
  };

  const getStatusDetails = (status: string) => {
    const s = (status || "").toLowerCase();
    if (s === "en_attente" || s === "attente") {
      return { label: "Reçue / En attente", step: 0, class: "en_attente" };
    }
    if (s === "en_cours" || s === "cours") {
      return { label: "En cours de traitement", step: 1, class: "en_cours" };
    }
    if (s === "resolu" || s === "résolu" || s === "resolue" || s === "résolue") {
      return { label: "Résolue", step: 2, class: "resolu" };
    }
    if (s === "ferme" || s === "fermée" || s === "fermee") {
      return { label: "Fermée / Archivée", step: 3, class: "ferme" };
    }
    return { label: "Inconnu", step: 0, class: "en_attente" };
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const screenWidth = window.innerWidth;
  const isMobileLayout = screenWidth <= 450;

  return (
    <div className="sr-page">
      {/* Show visitor sidebar/menu if visiteurId exists and it is not mobile and hideSidebar is false */}
      {visiteurId && !isMobileLayout && !hideSidebar && <MenuEspace />}

      <div style={{ marginLeft: visiteurId && !isMobileLayout && !hideSidebar ? "200px" : "0" }}>
        {/* Hero Section */}
        <header className="sr-hero">
          <button onClick={handleGoBack} className="sr-back-btn">
            <i className="bi bi-arrow-left"></i>
            <span>Retour</span>
          </button>

          <div className="sr-hero-badge">
            <i className="bi bi-headset"></i> Support Client
          </div>
          <h1 className="sr-hero-title">
            Assistance & <span>Réclamation</span>
          </h1>
          <p className="sr-hero-subtitle">
            Vous rencontrez un problème avec une commande ou un devis ? Notre équipe vous écoute et vous apporte des réponses dans les meilleurs délais.
          </p>

          {/* Navigation Tabs */}
          <div className="sr-tabs">
            <button
              className={`sr-tab-btn ${activeTab === "submit" ? "active" : ""}`}
              onClick={() => setActiveTab("submit")}
            >
              <i className="bi bi-file-earmark-plus"></i> Nouvelle demande
            </button>
            <button
              className={`sr-tab-btn ${activeTab === "track" ? "active" : ""}`}
              onClick={() => setActiveTab("track")}
            >
              <i className="bi bi-search"></i> Suivi & Historique
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="sr-content">
          <AnimatePresence mode="wait">
            {activeTab === "submit" && (
              <motion.div
                key="submit"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
              >
                {!submitSuccessCode ? (
                  <div className="sr-card">
                    <div className="sr-card-header">
                      <h2>
                        <i className="bi bi-envelope-paper"></i> Soumettre un problème
                      </h2>
                      <p>Remplissez les informations ci-dessous pour ouvrir un ticket de réclamation.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="sr-form">
                      {submitError && (
                        <div className="sr-alert error">
                          <i className="bi bi-exclamation-triangle"></i>
                          <span>{submitError}</span>
                        </div>
                      )}

                      <div className="sr-form-row">
                        <div className="sr-form-group">
                          <label className="sr-label">
                            Nom complet <span>*</span>
                          </label>
                          <input
                            type="text"
                            required
                            className="sr-input"
                            placeholder="Ex: Ahmed Alaoui"
                            value={nom}
                            onChange={(e) => setNom(e.target.value)}
                          />
                        </div>

                        <div className="sr-form-group">
                          <label className="sr-label">Adresse email</label>
                          <input
                            type="email"
                            className="sr-input"
                            placeholder="Ex: ahmed@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="sr-form-row">
                        <div className="sr-form-group">
                          <label className="sr-label">Numéro de téléphone</label>
                          <input
                            type="tel"
                            className="sr-input"
                            placeholder="Ex: +212 600 000 000"
                            value={telephone}
                            onChange={(e) => setTelephone(e.target.value)}
                          />
                        </div>

                        <div className="sr-form-group">
                          <label className="sr-label">
                            Référence commande ou devis <span style={{ color: "var(--text-muted-sr)", fontWeight: 500 }}>(Optionnel)</span>
                          </label>
                          <input
                            type="text"
                            className="sr-input"
                            placeholder="Ex: CMD-12345 or DEV-67890"
                            value={refCommande}
                            onChange={(e) => setRefCommande(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="sr-form-group mb-18" style={{ marginBottom: "18px" }}>
                        <label className="sr-label">
                          Type de problème <span>*</span>
                        </label>
                        <div className="sr-type-chips">
                          {PROBLEM_TYPES.map((type) => (
                            <div
                              key={type.value}
                              className={`sr-chip ${typeProbleme === type.value ? "selected" : ""}`}
                              onClick={() => setTypeProbleme(type.value)}
                            >
                              <i className={`bi ${type.icon}`}></i>
                              {type.label}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="sr-form-group mb-18" style={{ marginBottom: "18px" }}>
                        <label className="sr-label">
                          Description détaillée <span>*</span>
                        </label>
                        <textarea
                          required
                          className="sr-textarea"
                          placeholder="Décrivez avec précision votre réclamation pour nous aider à la résoudre rapidement..."
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>

                      <div className="sr-form-group mb-18" style={{ marginBottom: "20px" }}>
                        <label className="sr-label">Pièce jointe / Preuve (Photo, Capture d'écran)</label>
                        <div className="sr-upload-zone">
                          <i className="bi bi-cloud-arrow-up sr-upload-icon"></i>
                          <p className="sr-upload-text">
                            Glissez ou <span>parcourez</span> un fichier
                          </p>
                          <small style={{ color: "var(--text-muted-sr)", fontSize: "11px" }}>PNG, JPG ou JPEG</small>
                          <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={handleImageChange}
                          />
                        </div>

                        {imagePreview && (
                          <div style={{ textAlign: "center" }}>
                            <img src={imagePreview} alt="Preview" className="sr-preview-img" />
                            <div>
                              <button type="button" className="sr-remove-img-btn" onClick={removeImage}>
                                <i className="bi bi-trash"></i> Retirer l'image
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <button type="submit" className="sr-submit-btn" disabled={submitting}>
                        {submitting ? (
                          <>
                            <div className="sr-spinner"></div> Envoi en cours...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send-fill"></i> Soumettre ma réclamation
                          </>
                        )}
                      </button>
                    </form>

                    <div className="sr-info-box">
                      <i className="bi bi-info-circle-fill"></i>
                      <span>
                        Une fois envoyée, un code unique de suivi vous sera communiqué pour vous permettre de consulter l'évolution du traitement à tout moment.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="sr-card sr-success-wrap">
                    <div className="sr-success-icon">
                      <i className="bi bi-check-lg"></i>
                    </div>
                    <h2 className="sr-success-title">Réclamation envoyée !</h2>
                    <p className="sr-success-desc">
                      Votre demande a bien été enregistrée par notre équipe d'assistance. Notez précieusement votre numéro de ticket ci-dessous pour le suivi.
                    </p>

                    <div className="sr-code-box">
                      <span className="sr-code-label">Code de suivi</span>
                      <span className="sr-code-value">{submitSuccessCode}</span>
                      <button onClick={handleCopyCode} className="sr-code-copy-btn">
                        <i className="bi bi-clipboard"></i> Copier le code
                      </button>
                    </div>

                    <div className="sr-success-actions">
                      <button
                        onClick={() => {
                          setSubmitSuccessCode(null);
                        }}
                        className="sr-btn-outline"
                      >
                        <i className="bi bi-file-earmark-plus"></i> Créer un autre ticket
                      </button>
                      <button
                        onClick={() => {
                          setTrackCode(submitSuccessCode);
                          setActiveTab("track");
                          handleTrack(undefined, submitSuccessCode);
                          setSubmitSuccessCode(null);
                        }}
                        className="sr-btn-gold"
                      >
                        <i className="bi bi-eye-fill"></i> Suivre ce ticket
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === "track" && (
              <motion.div
                key="track"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
              >
                <div className="sr-card">
                  <div className="sr-card-header">
                    <h2>
                      <i className="bi bi-search"></i> Suivre une réclamation
                    </h2>
                    <p>Entrez le code de réclamation (REC-XXXXXX) pour consulter l'avancement.</p>
                  </div>

                  <div className="sr-track-form">
                    <form onSubmit={(e) => handleTrack(e)}>
                      <div className="sr-track-input-group">
                        <input
                          type="text"
                          required
                          className="sr-input"
                          placeholder="Entrez le code REC-XXXXXX"
                          value={trackCode}
                          onChange={(e) => setTrackCode(e.target.value)}
                        />
                        <button
                          type="submit"
                          className="sr-track-search-btn"
                          disabled={trackingLoading}
                        >
                          {trackingLoading ? (
                            <div className="sr-spinner"></div>
                          ) : (
                            <>
                              <i className="bi bi-search"></i> Rechercher
                            </>
                          )}
                        </button>
                      </div>
                    </form>

                    {trackingError && (
                      <div className="sr-alert error" style={{ marginTop: "20px" }}>
                        <i className="bi bi-exclamation-triangle"></i>
                        <span>{trackingError}</span>
                      </div>
                    )}

                    {/* Tracking Details View */}
                    {trackingResult && (
                      <div className="sr-result-card">
                        <div className="sr-result-top">
                          <div>
                            <span className="sr-result-code">{trackingResult.CodeReclamation}</span>
                            <div className="sr-result-nom">
                              Déposé par <strong>{trackingResult.Nom}</strong> le{" "}
                              {new Date(trackingResult.Date).toLocaleDateString("fr-FR", {
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </div>
                          </div>
                          <span className={`sr-status-badge ${getStatusDetails(trackingResult.Statut).class}`}>
                            <i className="bi bi-circle-fill" style={{ fontSize: "7px" }}></i>
                            {getStatusDetails(trackingResult.Statut).label}
                          </span>
                        </div>

                        {/* Visual timeline */}
                        <div className="sr-timeline">
                          <div className="sr-timeline-title">Progression de votre demande</div>
                          <div className="sr-timeline-steps">
                            <div className={`sr-timeline-step ${getStatusDetails(trackingResult.Statut).step >= 0 ? "done" : ""} ${getStatusDetails(trackingResult.Statut).step === 0 ? "active" : ""}`}>
                              <div className="sr-step-dot">
                                <i className="bi bi-check-lg"></i>
                              </div>
                              <span className="sr-step-label">Créée</span>
                            </div>
                            <div className={`sr-timeline-step ${getStatusDetails(trackingResult.Statut).step >= 1 ? "done" : ""} ${getStatusDetails(trackingResult.Statut).step === 1 ? "active" : ""}`}>
                              <div className="sr-step-dot">
                                <i className="bi bi-gear-fill"></i>
                              </div>
                              <span className="sr-step-label">En cours</span>
                            </div>
                            <div className={`sr-timeline-step ${getStatusDetails(trackingResult.Statut).step >= 2 ? "done" : ""} ${getStatusDetails(trackingResult.Statut).step === 2 ? "active" : ""}`}>
                              <div className="sr-step-dot">
                                <i className="bi bi-trophy-fill"></i>
                              </div>
                              <span className="sr-step-label">Résolue</span>
                            </div>
                          </div>
                        </div>

                        {/* Detail grids */}
                        <div className="sr-result-body">
                          <div className="sr-info-grid">
                            <div className="sr-info-item">
                              <label>Type de réclamation</label>
                              <span>
                                {PROBLEM_TYPES.find((t) => t.value === trackingResult.TypeProbleme)?.label ||
                                  trackingResult.TypeProbleme}
                              </span>
                            </div>
                            <div className="sr-info-item">
                              <label>Référence fournie</label>
                              <span>{trackingResult.RefCommande || "Aucune"}</span>
                            </div>
                            <div className="sr-info-item" style={{ gridColumn: "1 / -1" }}>
                              <label>Détails du problème</label>
                              <div className="sr-desc-box">{trackingResult.Description}</div>
                            </div>

                            {trackingResult.ImagePath && (
                              <div className="sr-info-item" style={{ gridColumn: "1 / -1" }}>
                                <label>Pièce jointe</label>
                                <a
                                  href={`${process.env.REACT_APP_PHP_APP_URL}/storage/${trackingResult.ImagePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    src={`${process.env.REACT_APP_PHP_APP_URL}/storage/${trackingResult.ImagePath}`}
                                    alt="Joint"
                                    className="sr-preview-img"
                                    style={{ maxWidth: "200px", marginTop: "5px" }}
                                  />
                                </a>
                              </div>
                            )}

                            {trackingResult.ReponseAdmin && (
                              <div className="sr-info-item" style={{ gridColumn: "1 / -1" }}>
                                <div className="sr-admin-response">
                                  <div className="sr-admin-response-label">
                                    <i className="bi bi-reply-fill"></i> Réponse de l'assistance
                                  </div>
                                  <div className="sr-admin-response-text">
                                    {trackingResult.ReponseAdmin}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* History list for connected visitors */}
                    {visiteurId && (
                      <div style={{ marginTop: "40px" }}>
                        <hr className="sr-divider" />
                        <h3
                          style={{
                            fontSize: "15px",
                            fontWeight: 800,
                            fontFamily: "Syne",
                            marginBottom: "15px",
                            color: "var(--text-pri-sr)",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <i className="bi bi-clock-history"></i> Vos réclamations précédentes
                        </h3>

                        {historyLoading ? (
                          <div>
                            <div className="sr-skeleton" style={{ width: "100%", height: "60px" }}></div>
                            <div className="sr-skeleton" style={{ width: "100%", height: "60px" }}></div>
                          </div>
                        ) : myReclamations.length > 0 ? (
                          <div className="sr-my-list">
                            {myReclamations.map((item) => (
                              <div
                                key={item.id}
                                className="sr-my-item"
                                onClick={() => handleSelectFromHistory(item.CodeReclamation)}
                              >
                                <div className="sr-my-item-left">
                                  <span className="sr-my-item-code">{item.CodeReclamation}</span>
                                  <div className="sr-my-item-type">
                                    {PROBLEM_TYPES.find((t) => t.value === item.TypeProbleme)?.label ||
                                      item.TypeProbleme}
                                  </div>
                                  <span className="sr-my-item-date">
                                    {new Date(item.Date).toLocaleDateString("fr-FR", {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric"
                                    })}
                                  </span>
                                </div>
                                <span className={`sr-status-badge ${getStatusDetails(item.Statut).class}`}>
                                  {getStatusDetails(item.Statut).label}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="sr-empty">
                            <i className="bi bi-folder-x sr-empty-icon"></i>
                            <p>Aucune réclamation enregistrée</p>
                            <small>Vos tickets de support s'afficheront ici.</small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Show mobile bottom nav if visitor is logged in and hideSidebar is false */}
      {visiteurId && isMobileLayout && !hideSidebar && <MenuEspace />}
    </div>
  );
}
